import { useContext, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as WoltDriveSVG } from "../../../../../../../../../../../../images/svg/bills/source_logos/wolt-logo.svg";
import { ReactComponent as WoltLoadingSVG } from "../../../../../../../../../../../../images/svg/liveOrders/wolt-loading.svg";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	AssignEmployeeDriverFormProps,
	DeliveryEstimationApi,
	LiveOrderApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { useKeepSendingRequest } from "../../../../../../hooks/useKeepSendingRequest";
import { OrdersContext } from "../../../../../../services/context";
import { UseKeepSendingRequestState } from "../../../../../../services/types";
import { getLiveOrderNumber, getNumberOfMinutesFromNowToTheDate } from "../../../../../../services/utils";
import { LiveOrderWoltChangeDataModalTimeOptions } from "./LiveOrderWoltChangeDateModalTimeOptions";

interface Props {
	onHide: () => void;
	order: LiveOrderApi;
}

const LiveOrderWoltChangeDateModal = ({ onHide, order }: Props) => {
	const [loading, setLoading] = useState(false);
	const [estimatingLoading, setEstimatingLoading] = useState(false);
	const [estimatedDeliveryData, setEstimatedDeliveryData] = useState<DeliveryEstimationApi | undefined>();
	const [selectedTimeOption, setSelectedTimeOption] = useState<number | undefined>(undefined);
	const [customMinutesValue, setCustomMinutesValue] = useState<number | undefined>(undefined);
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const { currency } = useSelector(selectOrganization);
	const confirmation = useConfirmation();
	const ordersContext = useContext(OrdersContext);
	const abortNumberInputEstimates = useRef<AbortController | undefined>(undefined);

	const getItemToDisplay = (): {
		time: string;
		price: MoneyApi | undefined;
	} => {
		const numberOfMinutesFromNowToTheEstimatedPreparationDate = getNumberOfMinutesFromNowToTheDate(
			order.estimated_preparation_at
		);

		if (selectedTimeOption || customMinutesValue) {
			const formSelectedValue = selectedTimeOption ?? customMinutesValue ?? 0;

			return {
				time: (numberOfMinutesFromNowToTheEstimatedPreparationDate + formSelectedValue).toString(),
				price: {
					amount: estimatedDeliveryData?.price?.amount ?? 0,
					currency: estimatedDeliveryData?.price?.currency ?? "PLN",
				},
			};
		}

		return {
			time: numberOfMinutesFromNowToTheEstimatedPreparationDate.toString(),
			price: { amount: 0, currency: currency ?? "PLN" },
		};
	};

	const getSelectedNumberOfMinutes = (): number => {
		const currentFormValue = selectedTimeOption ?? customMinutesValue ?? 0;
		const numberOfMinutesFromNowToTheEstimatedPreparationDate = getNumberOfMinutesFromNowToTheDate(
			order.estimated_preparation_at
		);
		return currentFormValue + numberOfMinutesFromNowToTheEstimatedPreparationDate;
	};

	const handleChangeDate = async (sendingRequestState?: UseKeepSendingRequestState) => {
		const { isSendingRequests, allRequestsSent, forceStopSendingRequests } = sendingRequestState ?? {};
		let firstRequestStartTimestamp = 0;
		try {
			if (!isSendingRequests) {
				await confirmation({
					title: t("lib:common.word.confirmation"),
					message: t("modules.live_order.field.wolt_extra_charge.title"),
				});
			}
			setLoading(true);

			const orderDelayInMinutes = getSelectedNumberOfMinutes();
			const data: AssignEmployeeDriverFormProps = {
				delivery_employee_id: estimatedDeliveryData?.id ?? order?.delivery?.delivery_employee?.reference_id,
				delivery_cost: {
					amount: estimatedDeliveryData?.price?.amount || 0,
					currency: currency ?? "PLN",
				},
				delivery_pickup_in_minutes: orderDelayInMinutes,
			};
			const params: Record<string, string> = {
				include: "delivery",
			};
			firstRequestStartTimestamp = performance.now();
			const res = await apiOrganization.assignEmployeeDeliveryToOrder(order.id, data, params);
			const newOrder = {
				...order,
				delivery: res.delivery,
				delivery_cost: res.delivery_cost,
				estimated_preparation_at: res.estimated_preparation_at,
				execution_at: res.execution_at,
				pickup_at: res.pickup_at,
			};

			ordersContext.updateOrder(newOrder);
			addSuccessFlash(t("lib:common.flash.saved"));
			setLoading(false);
			onHide();
		} catch (err: any) {
			const wrongTerminalError = err.errors?.find(
				(error: Record<string, any>) => error.code === "wrong_order_terminal"
			);
			if (wrongTerminalError) {
				if (!isSendingRequests) {
					keepSendingRequests(firstRequestStartTimestamp);
				}

				if (allRequestsSent) {
					const flashMessage = t("constraints.wrong_terminal_order", {
						terminal_name: wrongTerminalError.terminal_name,
					});
					addFlash({
						type: "danger",
						msg: flashMessage,
						messageAlreadyCreated: true,
					});
				} else {
					return;
				}
			} else {
				handleError.alert(err, addFlash);
			}
			setLoading(false);
		}
		if (forceStopSendingRequests !== undefined) {
			forceStopSendingRequests.current = true;
		}
	};

	const { keepSendingRequests } = useKeepSendingRequest((sendingRequestState: UseKeepSendingRequestState) =>
		handleChangeDate(sendingRequestState)
	);

	const handleChangeTimeOption = async (option: number) => {
		setCustomMinutesValue(undefined);
		getEstimatedDeliveryData(option);
	};

	const handleChangeCutomMinutesInput = async (floatValue?: number) => {
		setCustomMinutesValue(floatValue);

		if (!floatValue) return;

		setSelectedTimeOption(undefined);
		if (abortNumberInputEstimates) {
			abortNumberInputEstimates.current?.abort();
		}
		abortNumberInputEstimates.current = new AbortController();

		if (floatValue) {
			getEstimatedDeliveryData(floatValue);
		}
	};

	const getEstimatedDeliveryData = async (minutes: number) => {
		try {
			const numberOfMinutesFromNowToTheEstimatedPreparationDate = getNumberOfMinutesFromNowToTheDate(
				order.estimated_preparation_at
			);
			setEstimatingLoading(true);
			const res = await apiOrganization.estimateOrderDelivery(
				order.id,
				"WOLT_DRIVE",
				minutes + numberOfMinutesFromNowToTheEstimatedPreparationDate,
				undefined,
				{ abortController: abortNumberInputEstimates.current }
			);
			setEstimatedDeliveryData(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		} finally {
			setEstimatingLoading(false);
		}
	};

	const getIcon = () => {
		if (estimatingLoading) return <WoltLoadingSVG />;

		return <WoltDriveSVG />;
	};

	const { price, time } = getItemToDisplay();

	return (
		<Modal show onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					{`${t("modules.live_order.field.changing_preparation_status_date.title")} ${getLiveOrderNumber(
						order
					)}`}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="live-order-delivery-options">
					<h5>{t("modules.live_order.field.courier.title")}</h5>
					<div className="live-order-delivery-option live-order-delivery-option--selected">
						<div className="d-flex align-items-center">
							<div className="logo">{getIcon()}</div>
							<div>
								{estimatingLoading ? (
									<p
										className="title text-truncate text-primary"
										title={t("modules.live_order.field.estimating_delivery.title")}
									>
										{t("modules.live_order.field.estimating_delivery.title")}
									</p>
								) : (
									<>
										{Boolean(price?.amount) && (
											<p className="price text-truncate" title={FormatMoney(price)}>
												{FormatMoney(price)}
											</p>
										)}
										{time && (
											<p className="time text-truncate" title={time}>
												{`${time} ${t("lib:enums.dates.type.short.MINUTES")}`}
											</p>
										)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
				<hr />
				<LiveOrderWoltChangeDataModalTimeOptions
					selectedTimeOption={selectedTimeOption}
					setSelectedTimeOption={setSelectedTimeOption}
					estimatedPreparationAt={order.estimated_preparation_at}
					onChangeTimeOption={handleChangeTimeOption}
					customMinutesValue={customMinutesValue}
					onChangeCutomMinutesInput={handleChangeCutomMinutesInput}
				/>
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading
					loading={loading}
					onClick={() => handleChangeDate()}
					disabled={!selectedTimeOption && !customMinutesValue}
				>
					{t("common.action.save", { ns: "lib" })}
				</ButtonLoading>
				<Button onClick={onHide} variant="light">
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default LiveOrderWoltChangeDateModal;
